import { isFieldId } from '../intake.js';
import { KEY_RE } from './key-rule.js';
/**
 * Question keys: their format and the fixed bindings between client-row columns and keys. The single
 * source of these constants: if the generated library names a key differently, scripts/library-import
 * fails and the constants here change with it (spec 1.1).
 */
/** `<section>.<snake>` (shared shelf) or `custom.<slug>_<hash6>` (agency shelf). Mirrors the SQL CHECK. */
export { KEY_RE };
export const isCanonicalKey = (k) => KEY_RE.test(k);
/** The longest agency field id (intake.ts fieldIdSchema, THE field id rule). */
const FIELD_ID_MAX = 48;
/** FNV-1a, 32 bit, over the UTF-8 bytes of `s`, as 8 lowercase hex digits. Stable across runtimes. */
export function fnv1a32hex(s) {
    let h = 0x811c9dc5;
    for (const byte of new TextEncoder().encode(s)) {
        h ^= byte;
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}
/** Lowercase ASCII snake case of a label, at most `max` characters, never starting or ending in `_`. */
export function snakeSlug(label, max) {
    return label
        .normalize('NFKD')
        .replace(/\p{M}+/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, max)
        .replace(/_+$/g, '');
}
/** Agency-shelf key: 'custom.' + slug(label, 40) + '_' + fnv1a32hex(seed).slice(0, 6). Deterministic for a seed. */
export function customKeyFor(label, seed) {
    return `custom.${snakeSlug(label, 40) || 'question'}_${fnv1a32hex(seed).slice(0, 6)}`;
}
/** Field ids starting with `__` are agency metadata (`__headshot`) or portable extras (`__bio`), never a question's own id. */
export const isReservedFieldId = (id) => id.startsWith('__');
/**
 * The agency field id a key projects to when the agency has none: its first legacy id (a valid,
 * non-reserved one), else the key with '.' -> '__' trimmed of leading underscores; when that is longer
 * than 48 characters, the first 41 plus '_' plus fnv1a32hex(key).slice(0, 6), so two long keys never
 * collide. Never starts with '__'.
 *
 * With `taken` (a new library form item, spec 3.20: every id the agency already uses, in its schema, any of its
 * forms or its aliases), that id suffixed `_2`, `_3`, ... when it is taken (freeFieldId). An id the agency already
 * asks this same key by is its own: callers reuse it rather than pass it as taken.
 */
export function fieldIdForKey(key, legacyIds, taken) {
    const legacy = legacyIds.find((id) => isFieldId(id) && !isReservedFieldId(id));
    const id = key.replace(/\./g, '__').replace(/^_+/, '');
    const base = legacy ?? (id.length <= FIELD_ID_MAX ? id : `${id.slice(0, FIELD_ID_MAX - 7)}_${fnv1a32hex(key).slice(0, 6)}`);
    return taken ? freeFieldId(base, taken instanceof Set ? taken : new Set(taken)) : base;
}
/** `base`, else `base_2`, `base_3`, ...: the first id not taken, within the field id rule (the base is cut to fit). */
export function freeFieldId(base, taken) {
    if (!taken.has(base))
        return base;
    for (let n = 2;; n++) {
        const suffix = `_${n}`;
        const id = `${base.slice(0, FIELD_ID_MAX - suffix.length)}${suffix}`;
        if (!taken.has(id) && isFieldId(id))
            return id;
    }
}
/** The talent-fact columns of a clients row and the key each one holds (the library's real keys; the
 *  import fails when one is missing). full_name, email and phone are also the IMPLIED_KEYS below. */
export const CORE_COLUMN_KEYS = {
    full_name: 'basics.full_name',
    email: 'basics.email',
    phone: 'basics.phone',
    extra_phones: 'basics.extra_phones',
    headshot_url: 'media.primary_headshot',
};
const CORE_KEY_COLUMNS = new Map(Object.entries(CORE_COLUMN_KEYS).map(([column, key]) => [key, column]));
/** The clients column a core key is bound to (CORE_COLUMN_KEYS read backwards), or null for any other key. */
export const coreColumnOf = (key) => CORE_KEY_COLUMNS.get(key) ?? null;
/** Name parts forms ask for (PROFILE_CORE_IDS first_name, last_name). Stored as talent values (storage
 *  core) for prefill; never projected on their own: the writer joins them into basics.full_name. */
export const NAME_PART_KEYS = { first_name: 'basics.legal_first_name', last_name: 'basics.legal_last_name' };
/** Visible to a linked row with no grant while the talent has not left that agency. Mirrors SQL
 *  talent_implied_keys() exactly (0094, applied in production; a PG test pins them, so these keys never
 *  change without a migration). extra_phones is NOT implied: it is per_agency (an agency's parent and
 *  manager numbers, 0086). */
export const IMPLIED_KEYS = ['basics.full_name', 'basics.email', 'basics.phone'];
export const isImpliedKey = (key) => IMPLIED_KEYS.includes(key);
/** The reserved field id of the row's headshot metadata inside clients.profile (its crop and source photo). */
export const HEADSHOT_FIELD_ID = '__headshot';
/** The reserved field id of the portable bio inside profiles.profile. */
export const BIO_FIELD_ID = '__bio';
/** Agency metadata inside clients.profile: the crop and source of the row's headshot. */
export const RESERVED_PROFILE_KEYS = { [HEADSHOT_FIELD_ID]: 'media.primary_headshot' };
/** Lives in profiles.profile: the portable bio. */
export const PORTABLE_RESERVED_KEYS = { [BIO_FIELD_ID]: 'media.short_bio' };
/**
 * The key a reserved field id reads under (spec 3.1 rule 1): the headshot metadata of a row or the portable
 * bio (`store: 'portable'` = the portable store's own ids only). Null for any other id.
 */
export function reservedKeyOf(fieldId, store = 'any') {
    if (Object.hasOwn(PORTABLE_RESERVED_KEYS, fieldId))
        return PORTABLE_RESERVED_KEYS[fieldId];
    if (store === 'any' && Object.hasOwn(RESERVED_PROFILE_KEYS, fieldId))
        return RESERVED_PROFILE_KEYS[fieldId];
    return null;
}
const RESERVED_KEYS = [...Object.values(RESERVED_PROFILE_KEYS), ...Object.values(PORTABLE_RESERVED_KEYS)];
/** A key a reserved field id is bound to (the headshot, the bio): never granted (spec 3.4 step 5). */
export const isReservedKey = (key) => RESERVED_KEYS.includes(key);
/** Same-day agency alerts (Phase 5): how to reach the talent and their guardians, and the permit dates. */
export const CRITICAL_KEYS = Object.freeze([
    'basics.email',
    'basics.phone',
    'family.guardians',
    'family.guardian_1_email',
    'family.work_permit_expiry',
    'family.performer_permit_expiry',
]);
/**
 * The person-level child_performer_profiles columns that are a dependent's own answers (storage
 * `performer`), each under its own key. The form field work_permit_expiry is a different key
 * (family.work_permit_expiry): an agency's profile field and its performer panel can hold different dates
 * today. The encrypted columns (work_permit_number, coogan_account_last4) are identity vault kinds (Phase 4),
 * hours_restriction is computed and permit_reminded_on is bookkeeping, so none of those is a key.
 */
export const PERFORMER_COLUMN_KEYS = {
    entertainment_state: 'family.performer_work_state',
    work_permit_status: 'family.performer_permit_status',
    work_permit_expiry: 'family.performer_permit_expiry',
    coogan_account_bank: 'family.performer_coogan_bank',
    on_set_guardian_name: 'family.performer_on_set_guardian',
};
/** The grant key of each identity vault item kind but bank (spec 1.3). */
export const IDENTITY_KEYS = {
    ssn: 'money.ssn',
    ein: 'money.business_ein',
    permit: 'documents.minor_work_permit_number',
};
/** A bank item's grant key is per PURPOSE, so unsharing direct deposit never ends the Coogan grant. */
export const BANK_PURPOSE_KEYS = { direct_deposit: 'money.direct_deposit', coogan: 'family.coogan_account' };
const ownKey = (o, k) => Object.hasOwn(o, k);
/** The grant key of an identity vault item (spec 1.3): one per kind AND purpose; null for a login or an
 *  unknown kind or purpose. Phase 4's vault-kinds.ts builds on this one map. */
export function libraryKeyFor(kind, purpose) {
    if (kind === 'bank')
        return purpose && ownKey(BANK_PURPOSE_KEYS, purpose) ? BANK_PURPOSE_KEYS[purpose] : null;
    return ownKey(IDENTITY_KEYS, kind) ? IDENTITY_KEYS[kind] : null;
}
/** Every vault-stored key and its item kind: the import derives storage `vault` from this map only. */
export const VAULT_KEY_KINDS = Object.freeze({
    [IDENTITY_KEYS.ssn]: 'ssn',
    [IDENTITY_KEYS.ein]: 'ein',
    [IDENTITY_KEYS.permit]: 'permit',
    [BANK_PURPOSE_KEYS.direct_deposit]: 'bank',
    [BANK_PURPOSE_KEYS.coogan]: 'bank',
});
//# sourceMappingURL=keys.js.map